<template>
  <div class="nf-video-wrapper" @click.stop="toggleControler">
    <video
      class="video"
      :src="videoUrl"
      :webkit-playsinline="videoOptions.playsinline"
      :playsinline="videoOptions.playsinline"
      :x5-playsinline="videoOptions.playsinline"
      x-webkit-airplay="true"
      x5-video-player-type="h5-page"
      x5-video-player-fullscreen="true"
      preload="auto"
      :ref="`video_${videoKey}`"
      @durationchange="initTotalTime"
      @timeupdate="updateProgressTime"
      @ended="playEnded"
      @pause="videoPause"
      @play="videoPlay"
      @error="videoError"
    ></video>
    <div class="poster-bg-wrapper gaussian-blur" v-if="poster && showGuass">
      <img class="poster-bg" :src="poster" />
    </div>
    <img class="poster" :src="poster" v-show="showPoster && poster" />
    <div class="pagination-wrapper">
      <slot name="pagination"></slot>
    </div>
    <play-icon @click.stop="togglePlay" :playStatus="playStatus" :showPlayIcon="showPlayIconBeforePlay || showControler"></play-icon>
    <VideoControler
      v-model="showControler"
      :currentTime="videoOptions.currentTime"
      :totalTime="videoOptions.totalTime"
      :videoKey="videoKey"
      :progressBarScale="controlerOptions.progressBarScale"
      :isMuted="videoOptions.isMuted"
      @toggleMute="handleToggleMute"
      @fullscreen="handleFullscreen"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
      @updatePlayProgress="updatePlayProgress"
      @updateProgress="updateProgress"
      @toggleProgress="toggleProgress"
    ></VideoControler>
  </div>
</template>

<script>
import PlayIcon from './PlayIcon'
import VideoControler from './VideoControler'
import getRandomStr from './utils/getRandomStr'
import getSystemInfo from './utils/getSystemIno'
import { mapMutations } from 'vuex'

const CTRL_SHOW_TIME = 3000
const ERROR_MSG = '视频处理中，请稍后再试'

export default {
  name: 'nfVideo',
  props: {
    playInline: {
      type: Boolean,
      default: true
    },
    videoUrl: {
      type: String,
      default: ''
    },
    poster: {
      type: String,
      default: ''
    },
    videoKey: {
      type: String,
      default: getRandomStr()
    },
    showGuass: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showControler: false,
      playStatus: 'play',
      showPoster: true,
      showPlayIconBeforePlay: true,
      videoOptions: {
        currentTime: 0,
        totalTime: 0,
        isMuted: false,
        playsinline: true
      },
      controlerOptions: {
        progressBarScale: 0
      }
    }
  },
  methods: {
    ...mapMutations({
      showToast: 'toast/showToast'
    }),
    initVideo () {
      const systemInfo = getSystemInfo()
      const isAndroid = systemInfo.android
      this.videoOptions.playsinline = !isAndroid
    },
    togglePlay () {
      switch (this.playStatus) {
        case 'play':
          this.handlePlay()
          break
        case 'pause':
          this.handlePause()
          break
      }
    },
    toggleControler () {
      this.handleShowControler(!this.showControler)
      if (this.showControler) {
        this.autoHideControler()
      }
    },
    handleShowControler (status) {
      const newStatus = status && !this.showPlayIconBeforePlay
      this.showControler = newStatus
    },
    autoHideControler () {
      this.clearCtrlTimer()
      this.ctrlTimer = window.setTimeout(() => {
        if (this.playStatus === 'pause') {
          this.showControler = false
        }
      }, CTRL_SHOW_TIME)
    },
    clearCtrlTimer () {
      if (this.ctrlTimer) {
        window.clearTimeout(this.ctrlTimer)
        this.ctrlTimer = null
      }
    },
    handlePlay () {
      this.video.play()
      this.playStatus = 'pause'
      this.showPoster = false
      this.showControler = true
      this.autoHideControler()
    },
    handlePause () {
      this.video.pause()
      this.playStatus = 'play'
      this.handleShowControler(true)
      this.clearCtrlTimer()
    },
    initTotalTime () {
      this.video && (this.videoOptions.totalTime = this.video.duration)
    },
    updateProgressTime () {
      !this.isTouchCtrl && (this.videoOptions.currentTime = this.video.currentTime)
      !this.isTouchCtrl && this.updateProgress()
    },
    updateProgress (scaleX) {
      let progressBarScale = this.videoOptions.currentTime / this.videoOptions.totalTime
      if (scaleX !== undefined) {
        progressBarScale = scaleX
        this.videoOptions.currentTime = progressBarScale * this.videoOptions.totalTime
      }
      this.controlerOptions.progressBarScale = progressBarScale
    },
    playEnded () {
      this.video.currentTime = 0
      this.updateProgress()
      this.showPoster = true
      this.handleShowControler(true)
      this.playStatus = 'play'
    },
    handleToggleMute (status) {
      this.video.muted = status
      this.videoOptions.isMuted = status
      this.autoHideControler()
    },
    videoPause () {
      this.playStatus = 'play'
    },
    handleFullscreen () {
      if (!this.hasPlayed) return
      // ios全屏
      this.video.webkitEnterFullscreen()
      this.autoHideControler()
    },
    handleTouchStart () {
      this.isTouchCtrl = true
      this.clearCtrlTimer()
    },
    handleTouchEnd () {
      this.isTouchCtrl = false
      this.autoHideControler()
    },
    updatePlayProgress () {
      const currentTime = this.controlerOptions.progressBarScale * this.videoOptions.totalTime
      this.video.currentTime = currentTime
    },
    toggleProgress (scaleX) {
      this.updateProgress(scaleX)
      this.updatePlayProgress()
      this.autoHideControler()
    },
    videoPlay () {
      this.hasPlayed = true
      this.showPlayIconBeforePlay = false
    },
    videoError () {
      this.showToast(ERROR_MSG)
    }
  },
  components: {
    PlayIcon,
    VideoControler
  },
  computed: {
    videoRef () {
      return `video_${this.videoKey}`
    },
    video () {
      return this.$refs[this.videoRef]
    }
  },
  watch: {
    showControler (newVal) {
      this.$emit('toggleControler', newVal)
    }
  },
  created () {
    this.initVideo()
  }
}
</script>
<style lang="scss" scoped>
.nf-video-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  .video {
    height: 100%;
    width: 100%;
    object-fit: contain;
    position: relative;
    z-index: 1;
    background: transparent;
  }
  .poster-bg-wrapper {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 0;
    filter: blur(0.7rem);
    display: flex;
    align-items: center;
    justify-content: center;
    .poster-bg {
      width: 150%;
      height: 150%;
      object-fit: cover;
    }
  }
  .poster {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 2;
    object-fit: contain;
  }
  .pagination-wrapper {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 1;
  }
}
</style>
